<template>
  <div class="qr-code-reader qr-code-reader-absolute">
    <video
      ref="video"
      class="qrcode-video"
      :class="{ 'qrcode-video--hidden': !ready }"
      autoplay
      muted
      playsinline
    ></video>
    <canvas ref="overlay" class="qrcode-overlay"></canvas>

    <div class="loading-indicator" v-if="!ready && !error">
      Permita acesso a sua camêra
      <img src="@/assets/images/check-in/acesso-readqrcode.png" alt="arte-permitir-acesso">
    </div>

    <div class="error-indicator" v-if="error">
      <i class="fas fa-exclamation-triangle error-icon"></i>
      <div class="error-text">{{ errorMessage }}</div>
      <button v-if="error !== 'insecure'" type="button" class="error-retry" @click="retry">
        Tentar novamente
      </button>
    </div>

    <template v-if="ready">
      <div v-if="isReading" class="validation-pending">
        Validando...
      </div>
      <div class="reader-animation" :class="{'reader-animation-stop': isReading}">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </template>
  </div>
</template>

<script>
import BarcodeDetector from 'barcode-detector'

// Limita a frequência de detecção para ~25fps (40ms entre detect()).
// Mesmo valor usado pelo vue-qrcode-reader quando o tracking visual está
// ativo. Acima disso o ganho é imperceptível e custa bateria/CPU.
const DETECT_THROTTLE_MS = 40

// Cooldown após cada leitura processada — evita re-disparo instantâneo do
// QR ainda em frame e replica a pausa natural do ciclo camera off/on da
// implementação anterior. Após o cooldown, qualquer QR detectado (mesmo o
// recém-lido) volta a disparar; quem decide se "já fez check-in" é o backend.
const SCAN_COOLDOWN_MS = 800

// Lista de câmeras virtuais/auxiliares que devem ser ignoradas na heurística
// de seleção. Mesmo conjunto usado pelo vue-qrcode-reader.
const VIRTUAL_CAMERA_BLACKLIST = ['OBS Virtual Camera', 'OBS-Camera']

export default {
  name: 'scan-reader',
  props: {
    onRead: Function
  },
  data: () => ({
    isReading: false,
    ready: false,
    scanning: false,
    stream: null,
    detector: null,
    cooldownUntil: 0,
    lastDetectAt: 0,
    rafId: null,
    error: null
  }),
  computed: {
    errorMessage () {
      switch (this.error) {
        case 'insecure':
          return 'O acesso à câmera requer conexão segura (HTTPS).'
        case 'permission':
          return 'Acesso à câmera negado. Habilite a permissão nas configurações do navegador.'
        case 'no-camera':
          return 'Nenhuma câmera disponível neste dispositivo.'
        case 'in-use':
          return 'A câmera está em uso por outro aplicativo. Feche-o e tente novamente.'
        default:
          return 'Não foi possível inicializar a câmera.'
      }
    }
  },
  async mounted () {
    if (typeof window !== 'undefined' && !window.BarcodeDetector) {
      window.BarcodeDetector = BarcodeDetector
    }
    await this.initCamera()
  },
  beforeDestroy () {
    this.scanning = false
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    this.releaseStream()
  },
  methods: {
    async initCamera () {
      this.error = null

      // Verificação explícita de contexto seguro (HTTPS). getUserMedia falha
      // sob HTTP com NotAllowedError genérico, mascarando a causa real.
      if (typeof window !== 'undefined' && window.isSecureContext === false) {
        this.error = 'insecure'
        return
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.error = 'no-camera'
        return
      }

      try {
        const constraints = {
          audio: false,
          video: {
            width: { min: 360, ideal: 640, max: 1920 },
            height: { min: 240, ideal: 480, max: 1080 },
            ...(await this.pickRearCamera())
          }
        }

        try {
          this.stream = await navigator.mediaDevices.getUserMedia(constraints)
        } catch (e) {
          this.error = this.classifyMediaError(e)
          if (this.error === 'generic') console.error('getUserMedia falhou:', e)
          return
        }

        const video = this.$refs.video
        if (!video) {
          this.releaseStream()
          return
        }

        video.srcObject = this.stream
        await new Promise(resolve => {
          if (video.readyState >= 2) {
            resolve()
            return
          }
          video.addEventListener('loadeddata', resolve, { once: true })
        })
        try { await video.play() } catch (e) { /* autoplay coberto pelo atributo */ }

        // Alguns aparelhos abrem a câmera no ZOOM MÁXIMO (fica impossível ler de
        // perto). Zera o zoom e liga foco contínuo assim que o stream inicia.
        this.resetZoomAndFocus()

        this.detector = new BarcodeDetector({ formats: ['qr_code'] })
        this.ready = true
        this.scanning = true
        this.scanLoop()
      } catch (error) {
        console.error('Falha ao inicializar câmera:', error)
        this.error = 'generic'
      }
    },

    classifyMediaError (e) {
      if (!e || !e.name) return 'generic'
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError' || e.name === 'SecurityError') {
        return 'permission'
      }
      if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError' || e.name === 'OverconstrainedError') {
        return 'no-camera'
      }
      if (e.name === 'NotReadableError' || e.name === 'TrackStartError') {
        return 'in-use'
      }
      return 'generic'
    },

    // Phones modernos têm 3+ câmeras traseiras (wide, ultra-wide, telephoto).
    // Escolher a "última" pegava a TELEOBJETIVA em muitos aparelhos → abre com
    // zoom máximo, péssimo pra QR de perto. Estratégia:
    //  - Sem rótulos (permissão ainda não concedida): deixa o browser escolher a
    //    traseira padrão via `facingMode: environment` (geralmente a principal).
    //  - Com rótulos: escolhe a traseira PRINCIPAL, evitando tele/ultra/zoom.
    // Em qualquer caso, resetZoomAndFocus() ainda zera zoom digital residual.
    async pickRearCamera () {
      try {
        if (!navigator.mediaDevices.enumerateDevices) {
          return { facingMode: { ideal: 'environment' } }
        }
        const all = await navigator.mediaDevices.enumerateDevices()
        const videoInputs = all
          .filter(d => d.kind === 'videoinput')
          .filter(d => !VIRTUAL_CAMERA_BLACKLIST.includes(d.label || ''))
          .filter(d => !((d.label || '').toLowerCase().includes('infrared')))

        const hasLabels = videoInputs.some(d => (d.label || '').trim() !== '')
        if (!hasLabels || videoInputs.length === 0) {
          return { facingMode: { ideal: 'environment' } }
        }

        const label = d => (d.label || '').toLowerCase()
        const isRear = d => /back|rear|environment|traseir|arri[eè]re|tr[aá]s/.test(label(d))
        // Lentes a evitar (zoom/distorção). NÃO inclui "wide" puro — a principal
        // às vezes é rotulada "Back Wide Camera".
        const isZoomy = d => /tele|ultra|zoom|macro/.test(label(d))

        const rears = videoInputs.filter(isRear)
        const pool = rears.length ? rears : videoInputs
        const main = pool.find(d => !isZoomy(d)) || pool[0]
        if (main && main.deviceId) {
          return { deviceId: { exact: main.deviceId } }
        }
        return { facingMode: { ideal: 'environment' } }
      } catch (e) {
        return { facingMode: { ideal: 'environment' } }
      }
    },

    // Zera qualquer zoom (digital) e liga foco contínuo — corrige o bug de abrir
    // no zoom máximo. Tolerante: se o navegador/câmera não suportar, ignora.
    resetZoomAndFocus () {
      try {
        const track = this.stream && this.stream.getVideoTracks && this.stream.getVideoTracks()[0]
        if (!track || !track.getCapabilities || !track.applyConstraints) return
        const caps = track.getCapabilities()
        const advanced = []
        if (caps.zoom && typeof caps.zoom.min === 'number') {
          advanced.push({ zoom: caps.zoom.min })
        }
        if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
          advanced.push({ focusMode: 'continuous' })
        }
        if (advanced.length) {
          track.applyConstraints({ advanced }).catch(() => {})
        }
      } catch (e) { /* capabilities não suportadas — ignora */ }
    },

    releaseStream () {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
      const video = this.$refs.video
      if (video) {
        video.srcObject = null
      }
    },

    async retry () {
      this.releaseStream()
      this.ready = false
      this.scanning = false
      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
      await this.initCamera()
    },

    async scanLoop () {
      if (!this.scanning) return

      const video = this.$refs.video
      const now = Date.now()

      // Throttle explícito: ao menos DETECT_THROTTLE_MS entre chamadas a
      // detect(). Cada chamada consome CPU/bateria; sem isso o loop tentaria
      // detectar a 60fps no requestAnimationFrame.
      const canDetect =
        !this.isReading &&
        now >= this.cooldownUntil &&
        now - this.lastDetectAt >= DETECT_THROTTLE_MS &&
        this.detector &&
        video &&
        video.readyState >= 2

      if (canDetect) {
        this.lastDetectAt = now
        try {
          const detected = await this.detector.detect(video)
          if (!this.isReading && detected && detected.length > 0) {
            const code = detected[0].rawValue
            this.paintBoundingBox(detected)
            if (code) {
              this.handleDecode(code)
            }
          } else if (!detected || detected.length === 0) {
            this.clearOverlay()
          }
        } catch (e) { /* ignora erros transitórios de detect() */ }
      }

      if (this.scanning) {
        this.rafId = requestAnimationFrame(() => this.scanLoop())
      }
    },

    async handleDecode (content) {
      if (this.isReading) return
      this.isReading = true
      this.clearOverlay()
      try {
        await this.onRead(content)
      } finally {
        this.isReading = false
        this.cooldownUntil = Date.now() + SCAN_COOLDOWN_MS
      }
    },

    paintBoundingBox (detectedCodes) {
      const canvas = this.$refs.overlay
      const video = this.$refs.video
      if (!canvas || !video || !video.videoWidth || !video.videoHeight) return

      const displayW = video.offsetWidth
      const displayH = video.offsetHeight
      if (canvas.width !== displayW) canvas.width = displayW
      if (canvas.height !== displayH) canvas.height = displayH

      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, displayW, displayH)

      // Replica a transformação de coordenadas do vue-qrcode-reader para
      // acomodar object-fit: cover do <video>: o stream é "cortado" para
      // preencher o display, então as coordenadas de boundingBox precisam
      // ser escaladas pelo MAIOR dos ratios e centralizadas.
      const resW = video.videoWidth
      const resH = video.videoHeight
      const ratio = Math.max(displayW / resW, displayH / resH)
      const uncutW = resW * ratio
      const uncutH = resH * ratio
      const xOff = (displayW - uncutW) / 2
      const yOff = (displayH - uncutH) / 2

      ctx.lineWidth = 2
      ctx.strokeStyle = '#007bff'
      for (const code of detectedCodes) {
        if (!code.boundingBox) continue
        const { x, y, width, height } = code.boundingBox
        ctx.strokeRect(
          x * ratio + xOff,
          y * ratio + yOff,
          width * ratio,
          height * ratio
        )
      }
    },

    clearOverlay () {
      const canvas = this.$refs.overlay
      if (canvas && canvas.width && canvas.height) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~bootstrap/scss/functions";
@import "src/design/variables";

.qr-code-reader.qr-code-reader-absolute {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}

.qrcode-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.qrcode-video--hidden {
  visibility: hidden;
  position: absolute;
}

.qrcode-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.loading-indicator {
  padding-top: 12vh;
  font-weight: 400;
  font-size: 14px;
  text-align: center;

  img {
    margin-top: 35px;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  display: flex;
  flex-direction: column;
  align-items: center;
}

.error-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #292F42;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  gap: 16px;

  .error-icon {
    font-size: 42px;
    color: #F1B44C;
  }

  .error-text {
    font-size: 15px;
    font-weight: 400;
    max-width: 320px;
  }

  .error-retry {
    margin-top: 8px;
    background-color: #564AB1;
    color: #fff;
    border: 0;
    border-radius: 4px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
  }
}

.validation-pending {
  position: absolute;
  width: 100%;
  height: 100%;

  background-color: rgba(255, 255, 255, .8);
  text-align: center;
  font-weight: bold;
  font-size: 1.4rem;
  padding: 10px;

  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
}

.reader-animation {
  $espessura: 2%;
  $comprimento: 34%;

  --color: rgb(86, 74, 177);
  min-width: 200px;
  min-height: 200px;
  width: 40vmin;
  height: 40vmin;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none; /* guia decorativa: nunca deve interceptar cliques */

  div:nth-child(-n + 4) {
    position: absolute;
    width: 100%;
    height: 100%;

    &::before, &::after {
      top: 0;
      left: 0;
      position: absolute;
      content: '';
      display: block;
      width: $comprimento;
      height: $espessura;
      background-color: var(--color);
      border-radius: 100vmax;
    }

    &::after {
      height: $comprimento;
      width: $espessura;
    }
  }

  div:nth-child(2) {
    transform: rotate(90deg);
  }

  div:nth-child(3) {
    transform: rotate(180deg);
  }

  div:nth-child(4) {
    transform: rotate(270deg);
  }

  div:nth-child(5) {
    width: 80%;
    margin-left: 10%;
    height: $espessura;
    background-color: var(--color);
    border-radius: 100vmax;
    margin-top: 10%;
    animation: 0.8s linear 0s infinite alternate scanning;
    @keyframes scanning {
      from {
        margin-top: 10%;
      }
      to {
        margin-top: 90%;
      }
    }
  }

  &.reader-animation-stop div:nth-child(5) {
    animation-play-state: paused;
  }
}
</style>
